/** spec08: helpers for the Android app (pure, no platform APIs). */

export type Compatibility = "ok" | "update-app" | "update-server";

const parseVersion = (version: string) => {
  const match = /^(\d+)\.(\d+)\.\d+/.exec(version);
  return match ? [Number(match[1]), Number(match[2])] : null;
};

/**
 * FR9: the app and the server must share major and minor version; patches may differ.
 * Unparsable versions (dev builds report "dev") are treated as compatible.
 */
export function versionCompatibility(app: string, server: string): Compatibility {
  const a = parseVersion(app);
  const s = parseVersion(server);
  if (!a || !s) return "ok";
  const [appMajor = 0, appMinor = 0] = a;
  const [serverMajor = 0, serverMinor = 0] = s;
  if (appMajor === serverMajor && appMinor === serverMinor) return "ok";
  const appOlder = appMajor < serverMajor || (appMajor === serverMajor && appMinor < serverMinor);
  return appOlder ? "update-app" : "update-server";
}

/**
 * FR5: the server address the user typed, as an `https://` origin, or null when it is not
 * one. A bare host gets `https://`; paths, queries and credentials are not allowed.
 */
export function normalizeServerUrl(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  let url: URL;
  try {
    url = new URL(withScheme);
  } catch {
    return null;
  }
  if (url.protocol !== "https:" || url.username || url.password) return null;
  if ((url.pathname !== "/" && url.pathname !== "") || url.search || url.hash) return null;
  return url.origin;
}
